/**
 * Config das LPs de EMENTA (versão D) das 6 pós-graduações.
 *
 * Diferente das LPs de venda (A no Wix, B/C aqui), essa versão NÃO leva para o
 * checkout Eduzz: o objetivo único é capturar o lead pelo download da ementa
 * (tags `ementa-lp-*`) e alimentar o RD Station + RD CRM.
 *
 * Conteúdo baseado nas LPs A publicadas e nas ementas oficiais em PDF
 * (src/content/ementas/*.txt) — módulos, carga horária e coordenação reais.
 */

import type { ProductSlug } from "@/lib/ementas";

export type EmentaLPModulo = { n: string; titulo: string };

export type EmentaLPConfig = {
  slug: ProductSlug;
  /** Nome completo da pós (usado em H1/SEO) */
  nome: string;
  /** Nome curto (badges, CTAs) */
  nomeCurto: string;
  eyebrow: string;
  heroTitle: string;
  heroTitleHighlight: string;
  heroSubtitle: string;
  /** 4–5 competências que o aluno passa a dominar */
  competencias: string[];
  /** 3 perfis de quem é a pós */
  personas: { titulo: string; desc: string }[];
  coordenador: { nome: string; cargo: string; quote: string };
  /** Grade compacta */
  modulosLabel: string;
  modulos: EmentaLPModulo[];
  /** Link do time comercial (WhatsApp) */
  whatsappUrl: string;
  /** SEO */
  seoTitle: string;
  seoDescription: string;
};

const WHATSAPP = "https://nimble.li/o9yokpkm";

const CORE = {
  cargaHoraria: "360h",
  duracao: "12 a 15 meses",
  avista: "R$ 1.997",
  recorrente: "15x R$ 297",
};

export const FORMATO_ITENS = [
  { titulo: "Pós-graduação Lato Sensu", desc: "360h reconhecidas pelo MEC, sem TCC, em parceria com a Faculdade Vanguarda." },
  { titulo: "Aulas 100% online", desc: "Conteúdo assíncrono para começar imediatamente + 1 encontro ao vivo por mês." },
  { titulo: "Suporte dos professores", desc: "Suas dúvidas das aulas são respondidas pelos próprios docentes." },
  { titulo: "No seu ritmo", desc: "Até 15 meses para concluir, assistindo quando e onde quiser." },
];

export const EMENTA_LP_FAQ = [
  {
    q: "Preciso ser fisioterapeuta formado?",
    a: "Sim. A pós-graduação é exclusiva para fisioterapeutas formados. Estudantes de graduação podem baixar a ementa e se planejar para começar depois da formação.",
  },
  {
    q: "Como funcionam as aulas?",
    a: "São 100% online, com aulas gravadas liberadas progressivamente e um encontro ao vivo por mês. Você assiste no seu ritmo, com suporte direto dos professores.",
  },
  {
    q: "Qual a duração do curso?",
    a: "A especialização tem duração mínima de 12 meses e máxima de 15 meses. O acesso às aulas e materiais fica disponível por 15 meses a partir da matrícula.",
  },
  {
    q: "Tem TCC?",
    a: "Não. O MEC não exige TCC em cursos lato sensu, e optamos por ampliar a carga horária de conteúdo em vídeo em vez do trabalho de conclusão.",
  },
  {
    q: "O certificado é reconhecido?",
    a: "Sim. É um certificado de especialista em pós-graduação lato sensu reconhecido pelo MEC, emitido em parceria com a Faculdade Vanguarda.",
  },
];

export const EMENTA_LP_CORE = CORE;

export const EMENTA_LP_CONFIG: Record<ProductSlug, EmentaLPConfig> = {
  orto: {
    slug: "orto",
    nome: "Pós-graduação em Fisioterapia em Ortopedia e Traumatologia",
    nomeCurto: "Ortopedia e Traumatologia",
    eyebrow: "Pós-graduação · 360h · reconhecida pelo MEC",
    heroTitle: "Baixe a ementa completa da pós em Ortopedia e Traumatologia",
    heroTitleHighlight: "Ortopedia e Traumatologia",
    heroSubtitle:
      "Grade dos módulos, corpo docente, carga horária e formato de estudo em um PDF único, a formação que organiza o seu raciocínio clínico nas queixas musculoesqueléticas mais comuns.",
    competencias: [
      "Avaliar o paciente musculoesquelético com clareza, mesmo nos casos complexos",
      "Identificar bandeiras vermelhas e saber quando encaminhar",
      "Escolher a intervenção certa com base em diretrizes clínicas atuais",
      "Prescrever exercício com dose, carga e progressão adequadas",
    ],
    personas: [
      { titulo: "Fisio recém-formado", desc: "Que quer atender com segurança desde os primeiros pacientes, sem chutar conduta." },
      { titulo: "Fisio com prática clínica", desc: "Que quer atualizar o raciocínio com base em evidências e ganhar autoridade no consultório." },
      { titulo: "Fisio que quer se especializar", desc: "Que busca um título reconhecido pelo MEC e referência nacional em ortopedia." },
    ],
    coordenador: {
      nome: "Leandro Fukusawa",
      cargo: "Coordenação da pós em Ortopedia e Traumatologia",
      quote: "A pós não te ensina o que pensar, ela te ensina como pensar diante de cada paciente.",
    },
    modulosLabel: "12 módulos + módulos extras",
    modulos: [
      { n: "00", titulo: "Soft Skills e Profissionalismo" },
      { n: "01", titulo: "Prática Baseada em Evidências" },
      { n: "02", titulo: "Dor e Modelo Biopsicossocial" },
      { n: "03", titulo: "Princípios da Avaliação Musculoesquelética" },
      { n: "04", titulo: "Princípios das Intervenções Terapêuticas" },
      { n: "05", titulo: "Reabilitação da Coluna Vertebral" },
      { n: "06", titulo: "Reabilitação nos Membros Superiores" },
      { n: "07", titulo: "Reabilitação nos Membros Inferiores" },
      { n: "08", titulo: "Reabilitação nas Disfunções Orofaciais" },
      { n: "09", titulo: "Fisioterapia em Traumatologia" },
      { n: "10", titulo: "Oncologia Ortopédica" },
      { n: "11", titulo: "Práticas em Telerreabilitação" },
      { n: "EX", titulo: "Extras: Amputados dos Membros Inferiores e Ortopedia Pediátrica" },
    ],
    whatsappUrl: WHATSAPP,
    seoTitle: "Ementa da Pós em Ortopedia e Traumatologia | FEO",
    seoDescription:
      "Baixe em PDF a ementa completa da pós-graduação em Fisioterapia em Ortopedia e Traumatologia da FEO: 12 módulos, 360h, corpo docente e formato das aulas.",
  },

  dor: {
    slug: "dor",
    nome: "Especialização em Dor para Fisioterapeutas",
    nomeCurto: "Dor",
    eyebrow: "Especialização · 360h · reconhecida pelo MEC",
    heroTitle: "Baixe a ementa completa da especialização em Dor",
    heroTitleHighlight: "em Dor",
    heroSubtitle:
      "Os 14 módulos, os professores e o cronograma em um PDF único, a formação que organiza o seu raciocínio clínico na dor aguda, crônica e nos casos que ninguém resolveu.",
    competencias: [
      "Avaliar a dor com base em mecanismos, e não em rótulos",
      "Aplicar o modelo biopsicossocial sem cair no genérico",
      "Conduzir educação em dor que muda o comportamento do paciente",
      "Integrar exercício, terapia manual e abordagem cognitiva",
    ],
    personas: [
      { titulo: "Fisio que atende dor crônica", desc: "Que quer parar de tratar sintoma e começar a tratar mecanismo e comportamento." },
      { titulo: "Fisio em consultório", desc: "Que precisa de raciocínio robusto para casos complexos e recorrentes." },
      { titulo: "Fisio em formação contínua", desc: "Que quer referência científica atualizada com os maiores pesquisadores do Brasil." },
    ],
    coordenador: {
      nome: "Rafael Alaiti",
      cargo: "Coordenação da especialização em Dor",
      quote: "Dor não se trata com receita, se trata com raciocínio. E raciocínio se aprende com método.",
    },
    modulosLabel: "14 módulos",
    modulos: [
      { n: "00", titulo: "Soft Skills e Profissionalismo" },
      { n: "01", titulo: "Epidemiologia e Aspectos Sociais da Dor" },
      { n: "02", titulo: "Ciência Moderna da Dor" },
      { n: "03", titulo: "Interdisciplinaridade da Dor" },
      { n: "04", titulo: "Avaliação e Raciocínio Clínico na Dor" },
      { n: "05", titulo: "Intervenções Fisioterapêuticas para Dor" },
      { n: "06", titulo: "Dor Aguda" },
      { n: "07", titulo: "Dores Crônicas Primárias" },
      { n: "08", titulo: "Dor Secundária Musculoesquelética" },
      { n: "09", titulo: "Dor Neuropática e em Pacientes Neurológicos" },
      { n: "10", titulo: "Dor Crônica Secundária Pós-Trauma e Cirurgia" },
      { n: "11", titulo: "Dores Secundárias Oncológicas" },
      { n: "12", titulo: "Dores Viscerais" },
      { n: "13", titulo: "Dores de Cabeça e Orofaciais" },
      { n: "14", titulo: "Tratamento de Dor" },
    ],
    whatsappUrl: WHATSAPP,
    seoTitle: "Ementa da Especialização em Dor | FEO",
    seoDescription:
      "Baixe em PDF a ementa completa da especialização em Dor para fisioterapeutas da FEO: 14 módulos, 360h, corpo docente e formato das aulas.",
  },

  esporte: {
    slug: "esporte",
    nome: "Pós-graduação em Fisioterapia Esportiva",
    nomeCurto: "Esporte",
    eyebrow: "Pós-graduação · 360h · reconhecida pelo MEC",
    heroTitle: "Baixe a ementa completa da pós em Fisioterapia Esportiva",
    heroTitleHighlight: "Fisioterapia Esportiva",
    heroSubtitle:
      "Os 13 módulos, os professores e o cronograma em um PDF único, a formação para avaliar, prevenir, reabilitar e devolver o atleta ao esporte com segurança.",
    competencias: [
      "Avaliar o atleta com ferramentas validadas e critérios objetivos",
      "Planejar prevenção de lesões dentro da rotina de treino",
      "Conduzir a reabilitação até o retorno ao esporte com critério de alta",
      "Atuar em emergências e em populações especiais",
    ],
    personas: [
      { titulo: "Fisio que atende atletas", desc: "Do amador ao profissional, e precisa de critério para liberar o retorno ao esporte." },
      { titulo: "Fisio de clube ou academia", desc: "Que quer integrar reabilitação e treinamento com linguagem comum." },
      { titulo: "Fisio clínico", desc: "Que quer ampliar a atuação para a população ativa e esportiva." },
    ],
    coordenador: {
      nome: "Bruno Saragiotto",
      cargo: "Coordenação da pós em Fisioterapia Esportiva",
      quote: "Performance é consequência de raciocínio clínico bem feito, não de protocolo decorado.",
    },
    modulosLabel: "13 módulos",
    modulos: [
      { n: "00", titulo: "Soft Skills e Profissionalismo" },
      { n: "01", titulo: "O Esporte e o Atleta" },
      { n: "02", titulo: "Princípios do Treinamento" },
      { n: "03", titulo: "Biomecânica no Esporte" },
      { n: "04", titulo: "Prevenção de Lesões" },
      { n: "05", titulo: "Ferramentas Avaliativas" },
      { n: "06", titulo: "Raciocínio Clínico na Reabilitação do Atleta" },
      { n: "07", titulo: "Raciocínio Clínico nas Principais Lesões Esportivas" },
      { n: "08", titulo: "Recursos Terapêuticos" },
      { n: "09", titulo: "Emergências no Esporte" },
      { n: "10", titulo: "Atividade Física em Populações Especiais" },
      { n: "11", titulo: "Saúde do Atleta" },
      { n: "12", titulo: "Esportes Específicos" },
    ],
    whatsappUrl: WHATSAPP,
    seoTitle: "Ementa da Pós em Fisioterapia Esportiva | FEO",
    seoDescription:
      "Baixe em PDF a ementa completa da pós-graduação em Fisioterapia Esportiva da FEO: 13 módulos, 360h, corpo docente e formato das aulas.",
  },

  geronto: {
    slug: "geronto",
    nome: "Pós-graduação em Fisioterapia em Gerontologia",
    nomeCurto: "Gerontologia",
    eyebrow: "Pós-graduação · 360h · reconhecida pelo MEC",
    heroTitle: "Baixe a ementa completa da pós em Fisioterapia em Gerontologia",
    heroTitleHighlight: "Gerontologia",
    heroSubtitle:
      "Os 12 módulos, os professores e o cronograma em um PDF único, a formação organizada pelos domínios da capacidade intrínseca da pessoa idosa.",
    competencias: [
      "Avaliar a pessoa idosa por domínios: vitalidade, mobilidade, cognição e sensorial",
      "Prevenir quedas e manejar fragilidade com protocolos atualizados",
      "Construir plano de intervenção que devolve autonomia funcional",
      "Atuar em atenção multimodal e em equipe interprofissional",
    ],
    personas: [
      { titulo: "Fisio que atende idosos", desc: "Em domicílio, clínica ou instituição, e quer sair do protocolo genérico." },
      { titulo: "Fisio de reabilitação funcional", desc: "Que precisa avaliar risco de queda e fragilidade com instrumentos validados." },
      { titulo: "Fisio que quer se especializar", desc: "Em uma das áreas que mais cresce com o envelhecimento da população." },
    ],
    coordenador: {
      nome: "Anamaria Siriani",
      cargo: "Coordenação da pós em Gerontologia",
      quote: "Envelhecer não é diagnóstico. É contexto. E todo idoso merece um plano feito pra ele.",
    },
    modulosLabel: "12 módulos",
    modulos: [
      { n: "00", titulo: "Soft Skills e Profissionalismo" },
      { n: "01", titulo: "Prática Baseada em Evidências" },
      { n: "02", titulo: "A Pessoa Idosa: do Indivíduo às Perspectivas Populacionais" },
      { n: "03", titulo: "Domínio Vitalidade" },
      { n: "04", titulo: "Domínio Mobilidade" },
      { n: "05", titulo: "Domínio Psicológico" },
      { n: "06", titulo: "Domínio Cognitivo" },
      { n: "07", titulo: "Domínio Sensorial" },
      { n: "08", titulo: "Condições da Pessoa Idosa I" },
      { n: "09", titulo: "Condições da Pessoa Idosa II" },
      { n: "10", titulo: "Intervenções" },
      { n: "11", titulo: "Atenção Multimodal" },
    ],
    whatsappUrl: WHATSAPP,
    seoTitle: "Ementa da Pós em Fisioterapia em Gerontologia | FEO",
    seoDescription:
      "Baixe em PDF a ementa completa da pós-graduação em Fisioterapia em Gerontologia da FEO: 12 módulos, 360h, corpo docente e formato das aulas.",
  },

  trauma: {
    slug: "trauma",
    nome: "Pós-graduação em Fisioterapia em Traumato-Ortopedia",
    nomeCurto: "Traumatologia",
    eyebrow: "Pós-graduação · 360h · reconhecida pelo MEC",
    heroTitle: "Baixe a ementa completa da pós em Fisioterapia em Traumato-Ortopedia",
    heroTitleHighlight: "Traumato-Ortopedia",
    heroSubtitle:
      "Os 9 módulos organizados por segmento, os professores e o cronograma em um PDF único, do princípio da consolidação óssea ao retorno funcional do paciente cirúrgico.",
    competencias: [
      "Entender consolidação óssea e escolher a conduta em cada fase",
      "Avaliar o paciente traumato-ortopédico com leitura de exames de imagem",
      "Conduzir a reabilitação de fraturas por segmento, do ombro ao pé",
      "Reconhecer e manejar complicações do trauma",
    ],
    personas: [
      { titulo: "Fisio que atende pós-operatório", desc: "E quer segurança na progressão de carga e mobilidade em cada fase." },
      { titulo: "Fisio de hospital ou ambulatório", desc: "Que acompanha o paciente do trauma agudo à alta funcional." },
      { titulo: "Fisio de consultório", desc: "Que recebe o paciente cirúrgico já operado e precisa retomar o plano." },
    ],
    coordenador: {
      nome: "Leandro Fukusawa",
      cargo: "Coordenação da pós em Traumato-Ortopedia",
      quote: "O pós-operatório bem feito começa antes da cirurgia. Quem entende isso muda o desfecho do paciente.",
    },
    modulosLabel: "9 módulos",
    modulos: [
      { n: "01", titulo: "Princípios do Trauma e Consolidação Óssea" },
      { n: "02", titulo: "Avaliação Geral em Traumato-Ortopedia" },
      { n: "03", titulo: "Fraturas do Complexo do Ombro" },
      { n: "04", titulo: "Fraturas do Cotovelo, Punho e Mão" },
      { n: "05", titulo: "Fraturas da Coluna Vertebral" },
      { n: "06", titulo: "Fraturas do Quadril" },
      { n: "07", titulo: "Fraturas do Joelho" },
      { n: "08", titulo: "Fraturas do Tornozelo e Pé" },
      { n: "09", titulo: "Temas Específicos e Complicações" },
    ],
    whatsappUrl: WHATSAPP,
    seoTitle: "Ementa da Pós em Traumato-Ortopedia | FEO",
    seoDescription:
      "Baixe em PDF a ementa completa da pós-graduação em Fisioterapia em Traumato-Ortopedia da FEO: 9 módulos, 360h, corpo docente e formato das aulas.",
  },

  prescricao: {
    slug: "prescricao",
    nome: "Pós-graduação em Prescrição de Exercícios",
    nomeCurto: "Prescrição de Exercícios",
    eyebrow: "Pós-graduação · 360h · reconhecida pelo MEC",
    heroTitle: "Baixe a ementa completa da pós em Prescrição de Exercícios",
    heroTitleHighlight: "Prescrição de Exercícios",
    heroSubtitle:
      "Os 8 módulos, os professores e o cronograma em um PDF único, a formação para prescrever exercício com dose, carga e progressão de verdade.",
    competencias: [
      "Definir dose, volume e intensidade com critério clínico",
      "Aplicar fisiologia do exercício e biomecânica na reabilitação",
      "Usar controle motor e comportamento a favor da adesão",
      "Adaptar a prescrição para populações específicas",
    ],
    personas: [
      { titulo: "Fisio que prescreve exercício todo dia", desc: "E quer parar de repetir protocolo para começar a dosar de verdade." },
      { titulo: "Fisio clínico musculoesquelético", desc: "Que quer resultados consistentes em dor e função." },
      { titulo: "Fisio que atende população ativa", desc: "E precisa progredir carga com segurança em qualquer fase." },
    ],
    coordenador: {
      nome: "Caio Sain Vallio",
      cargo: "Coordenação da pós em Prescrição de Exercícios",
      quote: "Exercício é remédio, mas só funciona se você acertar a dose.",
    },
    modulosLabel: "8 módulos",
    modulos: [
      { n: "00", titulo: "Prática Baseada em Evidências e Soft Skills" },
      { n: "01", titulo: "Fundamentos da Prescrição de Exercícios" },
      { n: "02", titulo: "Biomecânica Aplicada à Reabilitação" },
      { n: "03", titulo: "Fisiologia do Exercício Aplicada" },
      { n: "04", titulo: "Neurociência, Controle Motor e Comportamento" },
      { n: "05", titulo: "Dor, Comportamento e Estilo de Vida" },
      { n: "06", titulo: "Avaliação, Diagnóstico Cinético-Funcional e Raciocínio Clínico" },
      { n: "07", titulo: "Populações Específicas" },
    ],
    whatsappUrl: WHATSAPP,
    seoTitle: "Ementa da Pós em Prescrição de Exercícios | FEO",
    seoDescription:
      "Baixe em PDF a ementa completa da pós-graduação em Prescrição de Exercícios da FEO: 8 módulos, 360h, corpo docente e formato das aulas.",
  },
};

/** Ordem canônica das pós nas listagens da versão D. */
export const EMENTA_LP_ORDER: ProductSlug[] = ["orto", "dor", "esporte", "geronto", "trauma", "prescricao"];

/** Rota da LP de ementa (versão D) de cada pós. */
export function ementaLpPath(slug: ProductSlug) {
  return `/ementa-${slug}`;
}
